import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Save, 
  Receipt, 
  PercentOutlined,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';

const translations = {
  ar: {
    title: 'إعدادات الضرائب',
    subtitle: 'إدارة نظام الضرائب والإعدادات المتعلقة بها',
    taxSettings: 'إعدادات الضريبة',
    taxInfo: 'معلومات الضريبة',
    taxEnabled: 'تفعيل الضرائب',
    defaultTaxRate: 'نسبة الضريبة الافتراضية (%)',
    taxNumber: 'الرقم الضريبي',
    taxName: 'اسم الضريبة (English)',
    taxNameAr: 'اسم الضريبة (عربي)',
    includeInPrice: 'تضمين الضريبة في السعر',
    saveChanges: 'حفظ التغييرات',
    saveSuccess: 'تم حفظ الإعدادات بنجاح',
    saveError: 'فشل حفظ الإعدادات',
    enabled: 'مفعّل',
    disabled: 'غير مفعّل',
    status: 'الحالة'
  },
  en: {
    title: 'Tax Settings',
    subtitle: 'Manage tax system and related configurations',
    taxSettings: 'Tax Settings',
    taxInfo: 'Tax Information',
    taxEnabled: 'Enable Tax',
    defaultTaxRate: 'Default Tax Rate (%)',
    taxNumber: 'Tax Number',
    taxName: 'Tax Name (English)',
    taxNameAr: 'Tax Name (Arabic)',
    includeInPrice: 'Include Tax in Price',
    saveChanges: 'Save Changes',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings',
    enabled: 'Enabled',
    disabled: 'Disabled',
    status: 'Status'
  }
};

export default function TaxSettingsTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [formData, setFormData] = useState({
    taxEnabled: true,
    defaultTaxRate: 0,
    taxNumber: '',
    taxName: 'VAT',
    taxNameAr: 'ضريبة القيمة المضافة',
    includeInPrice: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'defaultTaxRate' ? parseFloat(value) || 0 : value)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const token = localStorage.getItem('token');
      await axios.put(`${config.API_BASE_URL}/CompanySettings`, {
        taxEnabled: formData.taxEnabled,
        taxRate: formData.defaultTaxRate,
        taxNumber: formData.taxNumber
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: t.saveSuccess });
    } catch (error: any) {
      setMessage({ type: 'error', text: t.saveError });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* NOT IMPLEMENTED — 3 issues pending:
          1. No useEffect to load existing values from API on open (always shows defaults).
          2. taxRate from CompanySettings is never auto-applied to new purchase invoices.
          3. taxName, taxNameAr, includeInPrice fields are never sent on save (silently lost).
      */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        {isRTL
          ? 'هذا التاب غير مفعل بالكامل — الإعدادات تُحفظ في قاعدة البيانات لكنها لا تُطبَّق على الفواتير تلقائياً'
          : 'This tab is not fully implemented — settings are saved to DB but not auto-applied to invoices'}
      </Alert>
      {/* Header */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Receipt sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, borderRadius: 2 }} 
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Status Card */}
        <Box>
          <Card 
            sx={{ 
              borderRadius: 3,
              background: formData.taxEnabled 
                ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(233, 30, 99, 0.1) 100%)',
              border: `2px solid ${formData.taxEnabled ? '#4caf50' : '#f44336'}`
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  {formData.taxEnabled ? (
                    <CheckCircle sx={{ fontSize: 40, color: '#4caf50' }} />
                  ) : (
                    <Cancel sx={{ fontSize: 40, color: '#f44336' }} />
                  )}
                  <Box>
                    <Typography variant="h6" fontWeight="600">
                      {t.status}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.taxEnabled ? t.enabled : t.disabled}
                    </Typography>
                  </Box>
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.taxEnabled}
                      onChange={handleChange}
                      name="taxEnabled"
                    />
                  }
                  label={t.taxEnabled}
                />
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Tax Settings Card */}
          <Box>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                <Box 
                  sx={{ 
                    bgcolor: 'rgba(245, 87, 108, 0.1)', 
                    p: 1.5, 
                    borderRadius: 2 
                  }}
                >
                  <PercentOutlined sx={{ color: '#f5576c', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" fontWeight="600">
                  {t.taxSettings}
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label={t.defaultTaxRate}
                  name="defaultTaxRate"
                  type="number"
                  value={formData.defaultTaxRate}
                  onChange={handleChange}
                  disabled={!formData.taxEnabled}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t.taxNumber}
                  name="taxNumber"
                  value={formData.taxNumber}
                  onChange={handleChange}
                  disabled={!formData.taxEnabled}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.includeInPrice}
                      onChange={handleChange}
                      name="includeInPrice"
                      disabled={!formData.taxEnabled}
                    />
                  }
                  label={t.includeInPrice}
                />
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Tax Info Card */}
        <Box>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                transform: 'translateY(-4px)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                <Box 
                  sx={{ 
                    bgcolor: 'rgba(240, 147, 251, 0.1)', 
                    p: 1.5, 
                    borderRadius: 2 
                  }}
                >
                  <Receipt sx={{ color: '#f093fb', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" fontWeight="600">
                  {t.taxInfo}
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label={t.taxName}
                  name="taxName"
                  value={formData.taxName}
                  onChange={handleChange}
                  disabled={!formData.taxEnabled}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t.taxNameAr}
                  name="taxNameAr"
                  value={formData.taxNameAr}
                  onChange={handleChange}
                  disabled={!formData.taxEnabled}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Box>
        </Box>
      </Stack>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(240, 147, 251, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97edf 0%, #d9475a 100%)',
              boxShadow: '0 6px 20px rgba(240, 147, 251, 0.6)',
            }
          }}
        >
          {t.saveChanges}
        </Button>
      </Box>
    </Box>
  );
}